import { EnergyPulse, RADIUS, SUPPORT_EMAIL, WithHalfdot } from "@elyxeer/ui";
import LogoutIcon from "@mui/icons-material/Logout";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { DeleteAccountDrawer } from "~/components/account/DeleteAccountDrawer";
import { useProductPage } from "~/components/membership/use-product-page";
import { track } from "~/lib/analytics";
import { fetchOfferings } from "~/lib/api";
import { isGated } from "~/lib/membership";
import { queryKeys } from "~/lib/query-client";
import { useFireOnce } from "~/lib/use-fire-once";
import { useAuthStore } from "~/store/auth-store";
import { AnalyticsEventName } from "~/types/analytics";

/** Shared inline gutters keep the header, scroll area and dock aligned. */
const GUTTERS = { xs: 2.5, md: 5 } as const;

/** Shopify's CDN resizes on demand; the originals are print-sized megabytes. */
function sized(url: string, width: number): string {
  return `${url}${url.includes("?") ? "&" : "?"}width=${width}`;
}

/**
 * The hard paywall — the whole app for a signed-in non-member.
 *
 * Standalone by design: it renders outside `AppShell`, so there is no tab bar and
 * nothing to navigate to; the router sends every other path here (see `MemberGate`)
 * and this screen sends members straight back. The exits are the supplement (the
 * docked button opens its product page on elyxeer.com) and the pinned header's
 * account actions. That drawer offers sign-out and permanent app-account deletion
 * even to a new non-member who cannot reach Profile.
 *
 * The pitch is the physical subscription, with the app as something that comes with
 * it — which is what the business is. The app sells nothing and assembles no
 * checkout: the product page is where the Elyxeer 30.5 subscription is ordered, the
 * same page a customer who never installs the app would use.
 */
export function PaywallRoute() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const signOut = useAuthStore((state) => state.signOut);
  const refreshMembership = useAuthStore((state) => state.refreshMembership);
  const [confirmingExit, setConfirmingExit] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const openProductPage = useProductPage();

  // The one screen every unentitled member sees. Its view count is the denominator
  // of the conversion funnel, which is why ingest is not behind the member gate.
  useFireOnce("paywall", () => {
    track(AnalyticsEventName.PaywallView, { subjectType: "shop" });
  });

  // Only for the product photos: the button works without them, falling back to
  // the storefront's home page when the handle never arrived.
  const { data } = useQuery({
    queryKey: queryKeys.shopOfferings,
    queryFn: ({ signal }) => fetchOfferings(signal),
  });

  // A never-completed Shopify lookup is gated, but it can recover without making the
  // member sign out and back in. Retry while this screen is visible; a confirmed
  // "none" needs no polling, and a confirmed "active" navigates away below.
  useEffect(() => {
    if (user?.membership_status !== "unknown" || user.has_membership_override)
      return;

    const retry = () => {
      if (document.visibilityState === "visible") void refreshMembership();
    };
    retry();
    const interval = window.setInterval(retry, 60_000);
    window.addEventListener("focus", retry);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", retry);
    };
  }, [
    refreshMembership,
    user?.has_membership_override,
    user?.membership_status,
  ]);

  // Membership arrived — post-checkout refresh, a webhook, or a stale deep link.
  if (!isGated(user)) return <Navigate to="/news" replace />;

  // Only a subscription grants access, so the subscribable product is the one shown.
  const product = (data?.items ?? []).find(
    (item) => item.variants[0]?.selling_plans[0],
  );

  return (
    <Box
      sx={{
        height: "100dvh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
      }}
      data-testid="membership.paywall"
    >
      {/* Pinned header — the title and the one exit stay put while the pitch
          scrolls underneath. */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          paddingInline: GUTTERS,
          paddingTop: "calc(12px + env(safe-area-inset-top))",
          paddingBottom: 1.5,
        }}
      >
        <Typography variant="H1" sx={{ color: "text.primary" }}>
          <WithHalfdot size={11}>{t("membership.title")}</WithHalfdot>
        </Typography>
        <IconButton
          aria-label={t("membership.paywall.exit")}
          onClick={() => setConfirmingExit(true)}
          data-testid="paywall.exit"
          sx={{ color: "text.secondary" }}
        >
          <LogoutIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Box
          sx={{
            paddingInline: GUTTERS,
            paddingTop: { xs: 1, md: 3 },
            paddingBottom: 3,
          }}
        >
          <Paper
            variant="outlined"
            data-testid="paywall.content"
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "100%",
              maxWidth: 640,
              marginInline: "auto",
              padding: { xs: 3, md: 4 },
            }}
          >
            <EnergyPulse
              size={240}
              opacity={0.22}
              blur={10}
              sx={{ position: "absolute", top: -100, right: -80, zIndex: 0 }}
            />
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                color: "text.primary",
              }}
            >
              {/* Restore the thumbnail strip, skipping the oversized lead shot. */}
              {product && product.image_urls.length > 1 ? (
                <Box
                  sx={{ display: "flex", gap: 1 }}
                  data-testid="paywall.images"
                >
                  {product.image_urls.slice(1, 4).map((url, index) => (
                    <Box
                      key={url}
                      component="img"
                      src={sized(url, 400)}
                      alt={index === 0 ? product.title : ""}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        aspectRatio: "1 / 1",
                        objectFit: "cover",
                        borderRadius: `${RADIUS.sm}px`,
                        display: "block",
                      }}
                    />
                  ))}
                </Box>
              ) : null}

              <Box>
                <Typography variant="T2">
                  <strong>{t("membership.paywall.status")}</strong>
                </Typography>
                <Typography variant="T2">
                  {t("membership.paywall.exclusive")}
                </Typography>
              </Box>

              <Typography variant="H2">
                {t("membership.paywall.benefitsTitle")}
              </Typography>
              {(["delivery", "access", "flexibility"] as const).map((key) => (
                <Typography key={key} variant="T2">
                  <Trans
                    i18nKey={`membership.paywall.benefits.${key}`}
                    components={{ strong: <strong /> }}
                  />
                </Typography>
              ))}

              <Box data-testid="paywall.support">
                <Typography variant="T2">
                  <strong>{t("membership.paywall.supportTitle")}</strong>
                </Typography>
                <Typography variant="T2">
                  <Trans
                    i18nKey="membership.paywall.supportBody"
                    values={{ email: SUPPORT_EMAIL }}
                    components={{
                      // Mail links are handed to the mail client; the external
                      // in-app browser only supports HTTP(S).
                      email: (
                        <Link
                          href={`mailto:${SUPPORT_EMAIL}`}
                          sx={{ color: "link.main", fontWeight: 700 }}
                        />
                      ),
                    }}
                  />
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Outside the scroll area so the button stays reachable above the safe area. */}
      <Box
        sx={{
          flexShrink: 0,
          paddingInline: GUTTERS,
          paddingTop: 1.5,
          paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 640,
            marginInline: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Button
            variant="contained"
            size="large"
            fullWidth
            endIcon={<OpenInNewIcon sx={{ fontSize: "1rem" }} />}
            onClick={() => void openProductPage(product?.handle)}
            data-testid="paywall.join"
          >
            {t("membership.paywall.join")}
          </Button>
          <Typography
            variant="T3"
            sx={{ color: "text.muted", textAlign: "center" }}
            data-testid="paywall.join-note"
          >
            {t("membership.paywall.joinNote")}
          </Typography>
        </Box>
      </Box>

      <Drawer
        anchor="bottom"
        open={confirmingExit}
        onClose={() => setConfirmingExit(false)}
        aria-labelledby="paywall-exit-title"
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "background.paper",
              backgroundImage: "none",
              borderTopLeftRadius: `${RADIUS.lg}px`,
              borderTopRightRadius: `${RADIUS.lg}px`,
              width: "100%",
              maxWidth: 520,
              marginInline: "auto",
            },
          },
        }}
      >
        <Box
          data-testid="paywall.exit-drawer"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: 3,
            paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
          }}
        >
          <Typography
            id="paywall-exit-title"
            variant="H3"
            sx={{ color: "text.primary" }}
          >
            {t("membership.paywall.exitTitle")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => void signOut()}
            data-testid="paywall.logout"
          >
            {t("membership.paywall.exitConfirm")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="large"
            onClick={() => {
              setConfirmingExit(false);
              setConfirmingDelete(true);
            }}
            data-testid="paywall.delete-account"
          >
            {t("profile.menu.deleteAccount")}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="large"
            onClick={() => setConfirmingExit(false)}
            data-testid="paywall.stay"
          >
            {t("membership.paywall.exitCancel")}
          </Button>
        </Box>
      </Drawer>

      <DeleteAccountDrawer
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
      />
    </Box>
  );
}
